import { Hymn } from "@/services/hymnsApi";
import { getHymnSlug, scrollToTop } from "@/utils";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  RefreshCw,
  Trophy,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface TopHymnsSectionProps {
  hymns: Hymn[];
  loading?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
}

const STORAGE_KEY_TOP_HYMNS_EXPANDED = "egarizim_top_hymns_expanded";

export const TopHymnsSection: React.FC<TopHymnsSectionProps> = ({
  hymns,
  loading = false,
  hasError = false,
  onRetry,
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY_TOP_HYMNS_EXPANDED);
        return saved === "true";
      } catch (e) {
        console.warn(
          "Failed to read top hymns expanded state from localStorage",
          e,
        );
      }
    }
    return false;
  });

  // Persist isExpanded state in localStorage
  useEffect(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        localStorage.setItem(
          STORAGE_KEY_TOP_HYMNS_EXPANDED,
          String(isExpanded),
        );
      } catch (e) {
        console.warn(
          "Failed to save top hymns expanded state to localStorage",
          e,
        );
      }
    }
  }, [isExpanded]);

  const handleOpenSong = (hymn: Hymn) => {
    scrollToTop();
    const slug = getHymnSlug(hymn.number, hymn.title);
    navigate(
      `/chants-desperance/${encodeURIComponent(hymn.section_code)}/${slug}`,
    );
  };

  const displayedHymns = isExpanded ? hymns : hymns.slice(0, 8);

  return (
    <section className="relative w-full py-6 sm:py-8 border-t border-slate-800/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-4 sm:mb-5 gap-2">
          <div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              <Trophy className="w-8 h-8 text-amber-400 inline" /> Top cantiques
            </h2>
            <p className="text-slate-300 text-xs sm:text-sm mt-1 max-w-2xl leading-relaxed">
              Les cantiques les plus aimés et entonnés lors de nos
              rassemblements et cultes d'adoration.
            </p>
          </div>
        </div>

        {/* Skeleton Loader */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 sm:gap-3">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 animate-pulse space-y-2.5"
              >
                <div className="flex items-center justify-between">
                  <div className="h-5 w-12 bg-slate-800 rounded" />
                  <div className="h-3 w-16 bg-slate-800/60 rounded" />
                </div>
                <div className="h-4 bg-slate-800 rounded w-4/5" />
                <div className="pt-2 border-t border-slate-800/80 flex justify-between">
                  <div className="h-3 bg-slate-800 rounded w-1/3" />
                  <div className="h-3 bg-slate-800 rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {!loading && hasError && (
          <div className="text-center py-8 px-5 bg-slate-900/80 rounded-2xl border border-amber-500/30 backdrop-blur-md shadow-xl max-w-lg mx-auto my-2">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-3 text-amber-400">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-white mb-1">
              Impossible de charger le top cantiques
            </h3>
            <p className="text-slate-300 text-xs mb-4 max-w-sm mx-auto leading-relaxed">
              La liste des cantiques recommandés n'a pas pu être récupérée pour
              le moment.
            </p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs shadow-md shadow-amber-500/20 transition-all hover:scale-105"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Réessayer</span>
              </button>
            )}
          </div>
        )}

        {/* Hymns Grid */}
        {!loading && !hasError && displayedHymns.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 sm:gap-3">
              {displayedHymns.map((hymn, idx) => {
                return (
                  <div
                    key={
                      hymn.id || `${hymn.section_code}-${hymn.number}-${idx}`
                    }
                    onClick={() => handleOpenSong(hymn)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleOpenSong(hymn);
                      }
                    }}
                    className="group relative flex flex-col justify-between p-3 sm:p-3.5 rounded-xl bg-slate-900/90 hover:bg-slate-800/95 border border-slate-800/80 hover:border-amber-400/60 shadow-md hover:shadow-lg hover:shadow-amber-500/5 backdrop-blur-md transition-all duration-200 cursor-pointer overflow-hidden transform hover:-translate-y-0.5 text-left"
                  >
                    {/* Liseré supérieur doré discret */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-500 via-sky-500 to-amber-500 opacity-60 group-hover:opacity-100 transition-opacity" />

                    <div>
                      {/* En-tête de la carte */}
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="px-2 py-0.5 rounded-md bg-slate-800 text-amber-300 font-extrabold text-sm sm:text-base border border-slate-700/60">
                          N° {hymn.number}
                        </span>
                      </div>

                      {/* Titre */}
                      <h3 className="font-bold text-white text-xs sm:text-sm group-hover:text-amber-300 transition-colors line-clamp-2 leading-snug min-h-[2.2rem] flex items-center">
                        {hymn.title}
                      </h3>
                    </div>

                    {/* Card Footer */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-800/80 text-xs">
                      <span className="font-semibold text-slate-400 group-hover:text-sky-300 transition-colors truncate max-w-[65%] text-[11px]">
                        {hymn.section || hymn.section_code}
                      </span>

                      <span className="inline-flex items-center gap-0.5 font-bold text-amber-400 group-hover:translate-x-0.5 transition-transform text-[11px]">
                        Voir
                        <ChevronRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Show More / Show Less Toggle button */}
            {hymns.length > 8 && (
              <div className="mt-5 flex justify-center">
                <button
                  type="button"
                  onClick={() => setIsExpanded((prev) => !prev)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-800/90 hover:bg-slate-700/90 border border-slate-700 hover:border-amber-400/50 text-white font-bold text-xs sm:text-sm shadow-md transition-all hover:scale-105"
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-3.5 h-3.5 text-amber-400" />
                      <span>Réduire l'affichage</span>
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3.5 h-3.5 text-amber-400" />
                      <span>Voir tous les {hymns.length} cantiques</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};
